import { useMemo } from 'react';

import { useOrgMembersQuery } from '@/features/org/hooks/use-org-members-query';
import { OrgRole } from '@/features/org/validation/org-role.schema';
import { useProjectMembersQuery } from '@/features/projects/shared/hooks/use-project-members-query';
import { ProjectMemberManagementData } from '@/features/projects/shared/types/project-member-management-types';
import { formatDate } from '@/shared/utils/format';

export const useProjectAccessors = (
  projectId: string,
): ProjectMemberManagementData[] => {
  const { data: projectMembers } = useProjectMembersQuery({
    projectId: projectId!,
  });
  const { data: orgMembers } = useOrgMembersQuery();

  return useMemo(() => {
    const explicitProjectMembers: ProjectMemberManagementData[] =
      projectMembers?.map((projectMember) => ({
        id: projectMember.org_member_id,
        name: projectMember.org_member.profile?.name || '',
        email: projectMember.org_member.profile?.email || '',
        role: projectMember.org_member.role,
        memberSince: formatDate({
          date: projectMember.created_at || projectMember.org_member.created_at,
          formatType: 'digits',
        }),
        type: 'member' as const,
        projectMember,
        orgMember: projectMember.org_member,
      })) || [];

    // Add org owners and admins
    const orgAdminsAndOwners: ProjectMemberManagementData[] =
      orgMembers
        ?.filter(
          (orgMember) =>
            orgMember.role === OrgRole.OWNER ||
            orgMember.role === OrgRole.ADMIN,
        )
        .map((orgMember) => ({
          id: orgMember.member_id,
          name: orgMember.profile?.name || '',
          email: orgMember.profile?.email || '',
          role: orgMember.role,
          memberSince: formatDate({
            date: orgMember.created_at,
            formatType: 'digits',
          }),
          type: 'org_member' as const,
          orgMember,
        })) || [];

    return [...explicitProjectMembers, ...orgAdminsAndOwners];
  }, [projectMembers, orgMembers, projectId]);
};
